/**
 * Main-thread Web MIDI adapter.
 *
 * Lifecycle:
 *   init()    → requests MIDIAccess (sysex disabled), binds statechange,
 *               attaches onmidimessage to every existing input.
 *   listDevices() → snapshot of current `MIDIDeviceInfo[]`.
 *   selectDevice(id|null) → which device to listen to. null = all detected
 *               devices. The selected ID persists internally so a hot-plug
 *               of that exact ID resumes listening without re-selection.
 *   onNoteOn / onNoteOff / onDevicesChanged → subscribe; multiple handlers
 *               allowed; returns an unsubscribe.
 *   destroy() → drops handlers, removes the per-input message handler,
 *               clears statechange.
 *
 * Permission:
 *   The browser surfaces its prompt during `requestMIDIAccess`. On denial
 *   (NotAllowedError / SecurityError), state flips to `"denied"` and the
 *   class never re-prompts on its own — the user has to flip the Settings
 *   toggle, which destroys + re-creates the instance. Same pattern as
 *   input-mic's PitchDetector.
 *
 * Device hot-plug:
 *   The `statechange` event fires on every plug/unplug. We rebind
 *   per-input message handlers on every event (cheap — typically <5
 *   inputs) and dispatch the new device list to subscribers. We do NOT
 *   poll.
 *
 * Note-on / note-off semantics:
 *   - Status byte high nibble: 0x90 = note-on, 0x80 = note-off.
 *   - Channel = status byte low nibble (0..15).
 *   - Note-on with velocity 0 is the standard MIDI shorthand for note-off.
 *     We fold both forms into the noteoff handler.
 *
 * Web MIDI is a browser API. If `navigator.requestMIDIAccess` is undefined
 * (Safari < 18, Firefox < 108, anywhere outside a browser), state goes to
 * `"unsupported"` so Settings can show the right message.
 */

import type {
  MIDIAccess,
  MIDIAccessProvider,
  MIDIInputDevice,
  MIDIPort,
} from "./midi-types.js";

export interface MidiNoteOn {
  /** MIDI note number, 0..127. */
  midi: number;
  /** Velocity, 1..127. (A noteon with velocity 0 is a noteoff and never reaches this handler.) */
  velocity: number;
  /** `performance.now()` at the time the message was received. */
  timestamp: number;
  /** MIDI channel, 0..15. */
  channel: number;
  /** ID of the source device — useful when listening to all devices. */
  deviceId: string;
}

export interface MidiNoteOff {
  midi: number;
  timestamp: number;
  channel: number;
  deviceId: string;
}

export interface MidiDeviceInfo {
  /** Stable across reconnects on the same browser session. */
  id: string;
  name: string;
  manufacturer: string;
  state: "connected" | "disconnected";
}

export type MidiInputState =
  | "idle"
  | "ready"
  | "denied"
  | "unsupported"
  | "error";

export interface MidiInput {
  /**
   * Lazy — `requestMIDIAccess()` may require a user gesture in some
   * browsers. Idempotent: subsequent calls return the existing access
   * without re-prompting. Throws if Web MIDI is unsupported (state
   * flips to `"unsupported"` first).
   */
  init(): Promise<void>;
  listDevices(): MidiDeviceInfo[];
  /**
   * `null` listens to every detected device. Otherwise messages are
   * filtered to the matching ID. Persists internally so device hot-plug
   * doesn't lose the selection.
   */
  selectDevice(id: string | null): void;
  getSelectedDevice(): string | null;
  onNoteOn(handler: (event: MidiNoteOn) => void): () => void;
  onNoteOff(handler: (event: MidiNoteOff) => void): () => void;
  /**
   * Fires after every statechange event with the new device snapshot.
   * Settings uses this to keep its picker live without polling.
   */
  onDevicesChanged(handler: (devices: MidiDeviceInfo[]) => void): () => void;
  destroy(): void;
  readonly state: MidiInputState;
}

interface MidiInputInternal extends MidiInput {
  /** For tests — inject a fake `requestMIDIAccess`. */
  _setMidiAccessProvider(provider: MIDIAccessProvider): void;
}

class MidiInputImpl implements MidiInputInternal {
  state: MidiInputState = "idle";

  private access: MIDIAccess | null = null;
  private accessProvider: MIDIAccessProvider | null;
  private selectedDeviceId: string | null = null;

  private noteOnHandlers: Set<(e: MidiNoteOn) => void> = new Set();
  private noteOffHandlers: Set<(e: MidiNoteOff) => void> = new Set();
  private devicesHandlers: Set<(devices: MidiDeviceInfo[]) => void> = new Set();

  /** IDs of inputs we've installed `onmidimessage` on. */
  private boundInputs: Set<string> = new Set();

  /** Promise from a prior init() — re-entrant init() shares it so we
   *  don't double-prompt for permission. */
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.accessProvider = (() => {
      const nav = (globalThis as { navigator?: Navigator }).navigator;
      const fn = (
        nav as unknown as { requestMIDIAccess?: MIDIAccessProvider } | undefined
      )?.requestMIDIAccess;
      // Bind to navigator so `this` is correct inside the browser impl.
      return fn ? fn.bind(nav as unknown as object) : null;
    })();
  }

  _setMidiAccessProvider(provider: MIDIAccessProvider): void {
    this.accessProvider = provider;
  }

  async init(): Promise<void> {
    if (this.access) return;
    if (this.initPromise) return this.initPromise;
    if (this.state === "denied") {
      // No auto-reprompt — the user has to destroy() this instance (the
      // Settings toggle re-creates a fresh one) before another permission
      // request can fire.
      throw new Error("MIDI permission was previously denied");
    }
    if (!this.accessProvider) {
      this.state = "unsupported";
      throw new Error("Web MIDI API is not available in this browser");
    }

    const provider = this.accessProvider;
    this.initPromise = (async () => {
      try {
        const access = await provider({ sysex: false });
        this.access = access;
        this.state = "ready";
        this.bindAllInputs();
        access.onstatechange = (event) => this.handleStateChange(event.port);
      } catch (err) {
        const name = (err as { name?: string }).name;
        if (name === "NotAllowedError" || name === "SecurityError") {
          this.state = "denied";
        } else {
          this.state = "error";
        }
        throw err;
      }
    })();

    this.initPromise.finally(() => {
      this.initPromise = null;
    }).catch(() => {
      // Handled by the original promise the caller awaits — avoid the
      // unhandled-rejection warning the chained .finally() promise would
      // otherwise produce. Same pattern as PitchDetector.start.
    });

    return this.initPromise;
  }

  listDevices(): MidiDeviceInfo[] {
    if (!this.access) return [];
    const out: MidiDeviceInfo[] = [];
    for (const input of this.access.inputs.values()) {
      out.push(toDeviceInfo(input));
    }
    return out;
  }

  selectDevice(id: string | null): void {
    this.selectedDeviceId = id;
  }

  getSelectedDevice(): string | null {
    return this.selectedDeviceId;
  }

  onNoteOn(handler: (event: MidiNoteOn) => void): () => void {
    this.noteOnHandlers.add(handler);
    return () => {
      this.noteOnHandlers.delete(handler);
    };
  }

  onNoteOff(handler: (event: MidiNoteOff) => void): () => void {
    this.noteOffHandlers.add(handler);
    return () => {
      this.noteOffHandlers.delete(handler);
    };
  }

  onDevicesChanged(handler: (devices: MidiDeviceInfo[]) => void): () => void {
    this.devicesHandlers.add(handler);
    return () => {
      this.devicesHandlers.delete(handler);
    };
  }

  destroy(): void {
    if (this.access) {
      // Drop our message handler on every bound input. Don't touch
      // anyone else's; we never assume exclusive ownership of the
      // input's event stream.
      for (const input of this.access.inputs.values()) {
        if (this.boundInputs.has(input.id)) {
          input.onmidimessage = null;
        }
      }
      this.access.onstatechange = null;
    }
    this.boundInputs.clear();
    this.noteOnHandlers.clear();
    this.noteOffHandlers.clear();
    this.devicesHandlers.clear();
    this.access = null;
    this.state = "idle";
  }

  private bindAllInputs(): void {
    if (!this.access) return;
    for (const input of this.access.inputs.values()) {
      this.bindInput(input);
    }
  }

  /**
   * Install our message handler on a MIDI input. Always assigns rather
   * than skipping — Web MIDI sets `onmidimessage = null` on disconnect,
   * so a reconnect (same id) needs the handler re-installed. The
   * boundInputs set is used for cleanup tracking, not for guarding
   * re-bind.
   */
  private bindInput(input: MIDIInputDevice): void {
    input.onmidimessage = (event) => {
      this.handleMidiMessage(input.id, event.data);
    };
    this.boundInputs.add(input.id);
  }

  private handleStateChange(port: MIDIPort): void {
    // statechange covers both `inputs` and `outputs`; only inputs are
    // interesting to us.
    if (port.type !== "input") return;
    if (this.access) {
      // Rebind every connected input. Cheap (typically <5 inputs); idempotent.
      for (const input of this.access.inputs.values()) {
        if (input.state === "connected") {
          this.bindInput(input);
        }
      }
    }
    this.notifyDevicesChanged();
  }

  private notifyDevicesChanged(): void {
    const snapshot = this.listDevices();
    for (const handler of this.devicesHandlers) handler(snapshot);
  }

  private handleMidiMessage(deviceId: string, data: Uint8Array): void {
    // Defensive — a malformed message shouldn't take the page down. We
    // care about [status, data1, data2] specifically.
    if (data.length < 3) return;
    const status = data[0]!;
    const command = status & 0xf0;
    const channel = status & 0x0f;
    const note = data[1]!;
    const velocity = data[2]!;

    // Filter by selected device. null = listen to everything.
    if (this.selectedDeviceId !== null && this.selectedDeviceId !== deviceId) {
      return;
    }

    const ts = nowMs();

    if (command === 0x90) {
      // Note-on with velocity 0 is the universal "running-status" form
      // of note-off. Many keyboards emit only this form and never send
      // a real 0x80 message.
      if (velocity === 0) {
        const event: MidiNoteOff = { midi: note, timestamp: ts, channel, deviceId };
        for (const handler of this.noteOffHandlers) handler(event);
      } else {
        const event: MidiNoteOn = {
          midi: note,
          velocity,
          timestamp: ts,
          channel,
          deviceId,
        };
        for (const handler of this.noteOnHandlers) handler(event);
      }
    } else if (command === 0x80) {
      const event: MidiNoteOff = { midi: note, timestamp: ts, channel, deviceId };
      for (const handler of this.noteOffHandlers) handler(event);
    }
    // Every other channel-voice message (CC, pitch bend, aftertouch,
    // program change) is ignored — we don't need them for grading.
  }
}

function toDeviceInfo(input: MIDIInputDevice): MidiDeviceInfo {
  return {
    id: input.id,
    name: input.name ?? "Unknown MIDI input",
    manufacturer: input.manufacturer ?? "",
    state: input.state,
  };
}

/**
 * Tries `performance.now()` first (sub-ms precision, tied to the
 * AudioContext clock the rest of the app runs on); falls back to
 * `Date.now()` in environments without `performance` (older Node, some
 * tests).
 */
function nowMs(): number {
  const perf = (globalThis as { performance?: { now: () => number } }).performance;
  return perf ? perf.now() : Date.now();
}

export function createMidiInput(): MidiInput {
  return new MidiInputImpl();
}

/**
 * For tests: create a MidiInput with a stubbed access provider so we
 * don't have to touch the real navigator.requestMIDIAccess API.
 */
export function createMidiInputWithAccessProvider(
  provider: MIDIAccessProvider,
): MidiInput {
  const m = new MidiInputImpl();
  m._setMidiAccessProvider(provider);
  return m;
}
